export type LightTagPerformanceModeKey =
  | 'DEFAULT'
  | 'HIGH_RESPONSIVENESS'
  | 'BATTERY_SAVER';

export type LightTagPerformanceProfileDisplayKey =
  | LightTagPerformanceModeKey
  | 'unknown';

export interface LightTagPerformanceConfiguration {
  isListening?: boolean | null;
  listeningActiveIntervalMs?: number | null;
  listeningActiveDutyMs?: number | null;
  listeningPassiveIntervalMs?: number | null;
  listeningPassiveDutyMs?: number | null;
  listeningSleepAfterSeconds?: number | null;
}

export type LightTagPerformanceModeDisplay =
  | {
    type: 'profile';
    profileKey: LightTagPerformanceProfileDisplayKey;
  }
  | {
    type: 'parameters';
    activeIntervalMs?: number | null;
    activeWindowMs?: number | null;
    passiveIntervalMs?: number | null;
    passiveWindowMs?: number | null;
    sleepAfterSeconds?: number | null;
  };

const LIGHT_TAG_PERFORMANCE_PROFILES: Array<{
  key: LightTagPerformanceModeKey;
  configuration: Required<Omit<LightTagPerformanceConfiguration, 'isListening'>>;
}> = [
  {
    key: 'DEFAULT',
    configuration: {
      listeningActiveIntervalMs: 1000,
      listeningActiveDutyMs: 34,
      listeningPassiveIntervalMs: 10000,
      listeningPassiveDutyMs: 34,
      listeningSleepAfterSeconds: 120,
    },
  },
  {
    key: 'HIGH_RESPONSIVENESS',
    configuration: {
      listeningActiveIntervalMs: 500,
      listeningActiveDutyMs: 34,
      listeningPassiveIntervalMs: 10000,
      listeningPassiveDutyMs: 34,
      listeningSleepAfterSeconds: 120,
    },
  },
  {
    key: 'BATTERY_SAVER',
    configuration: {
      listeningActiveIntervalMs: 2500,
      listeningActiveDutyMs: 34,
      listeningPassiveIntervalMs: 10000,
      listeningPassiveDutyMs: 34,
      listeningSleepAfterSeconds: 120,
    },
  },
];

export function lightTagPerformanceModeKey(
  configuration: LightTagPerformanceConfiguration,
): LightTagPerformanceModeKey | undefined {
  if (!isCompleteConfiguration(configuration)) {
    return undefined;
  }

  return LIGHT_TAG_PERFORMANCE_PROFILES.find((profile) =>
    profile.configuration.listeningActiveIntervalMs === configuration.listeningActiveIntervalMs &&
    profile.configuration.listeningActiveDutyMs === configuration.listeningActiveDutyMs &&
    profile.configuration.listeningPassiveIntervalMs === configuration.listeningPassiveIntervalMs &&
    profile.configuration.listeningPassiveDutyMs === configuration.listeningPassiveDutyMs &&
    profile.configuration.listeningSleepAfterSeconds === configuration.listeningSleepAfterSeconds,
  )?.key;
}

export function formatLightTagPerformanceConfiguration(
  configuration: LightTagPerformanceConfiguration,
): string | undefined {
  const values = lightTagPerformanceConfigurationValues(configuration);
  if (!values) {
    return undefined;
  }
  return `active ${values.active}, passive ${values.passive}, sleep after ${values.sleepAfter}`;
}

export function lightTagPerformanceConfigurationValues(
  configuration: LightTagPerformanceConfiguration,
): { active: string; passive: string; sleepAfter: string } | undefined {
  if (!hasPerformanceConfiguration(configuration)) {
    return undefined;
  }
  return {
    active: formatIntervalDuty(
      configuration.listeningActiveIntervalMs,
      configuration.listeningActiveDutyMs,
    ),
    passive: formatIntervalDuty(
      configuration.listeningPassiveIntervalMs,
      configuration.listeningPassiveDutyMs,
    ),
    sleepAfter: `${formatValue(configuration.listeningSleepAfterSeconds)} s`,
  };
}

export function lightTagPerformanceModeDisplay(
  configuration: LightTagPerformanceConfiguration,
  isAdmin: boolean,
): LightTagPerformanceModeDisplay | undefined {
  if (configuration.isListening !== true || !hasPerformanceConfiguration(configuration)) {
    return undefined;
  }

  if (isAdmin) {
    return {
      type: 'parameters',
      activeIntervalMs: configuration.listeningActiveIntervalMs,
      activeWindowMs: configuration.listeningActiveDutyMs,
      passiveIntervalMs: configuration.listeningPassiveIntervalMs,
      passiveWindowMs: configuration.listeningPassiveDutyMs,
      sleepAfterSeconds: configuration.listeningSleepAfterSeconds,
    };
  }

  return {
    type: 'profile',
    profileKey: lightTagPerformanceModeKey(configuration) ?? 'unknown',
  };
}

export function lightTagPerformanceProfileKey(
  configuration: LightTagPerformanceConfiguration,
): LightTagPerformanceProfileDisplayKey | undefined {
  if (configuration.isListening !== true || !hasPerformanceConfiguration(configuration)) {
    return undefined;
  }

  return lightTagPerformanceModeKey(configuration) ?? 'unknown';
}

export function hasPerformanceConfiguration(
  configuration: LightTagPerformanceConfiguration,
): boolean {
  return [
    configuration.listeningActiveIntervalMs,
    configuration.listeningActiveDutyMs,
    configuration.listeningPassiveIntervalMs,
    configuration.listeningPassiveDutyMs,
    configuration.listeningSleepAfterSeconds,
  ].some((value) => value !== null && value !== undefined);
}

function isCompleteConfiguration(
  configuration: LightTagPerformanceConfiguration,
): configuration is Required<LightTagPerformanceConfiguration> {
  return [
    configuration.listeningActiveIntervalMs,
    configuration.listeningActiveDutyMs,
    configuration.listeningPassiveIntervalMs,
    configuration.listeningPassiveDutyMs,
    configuration.listeningSleepAfterSeconds,
  ].every((value) => value !== null && value !== undefined);
}

function formatIntervalDuty(
  intervalMs: number | null | undefined,
  dutyMs: number | null | undefined,
): string {
  return `${formatValue(intervalMs)}/${formatValue(dutyMs)} ms`;
}

function formatValue(value: number | null | undefined): string {
  return value === null || value === undefined ? '-' : value.toString();
}
